// Common layout helpers and interactive views.

import { setTitle } from 'platform-specific/url';
import React, { useCallback, useState } from 'react';
import type { Role, StyleProp, ViewStyle } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import type { TLabel } from 'util/stdtypes';
import { makeTestID, undefFP, undefR, undefS, undefTL } from 'util/stdtypes';

import { colorBorderPrimary, colorSurfaceHighlight, colorSurfacePrimary } from './color';
import { useTranslation } from './translation';

const MIN_HEIGHT_CLICKZONE = 44;
const MIN_WIDTH_CLICKZONE = 44;

export type PadProps = { size?: number };

export function Pad({ size = 20 }: PadProps) {
    return <View style={{ height: size, width: size }} />;
}

export type HoverViewProps = {
    style?: StyleProp<ViewStyle>;
    ariaLabel?: TLabel;
    ariaText?: string;
    testID?: string;
    role?: Role;
    hoverStyle?: StyleProp<ViewStyle>;
    pressedStyle?: StyleProp<ViewStyle>;
    children: React.ReactNode;
    disabled?: boolean;
    onPress?: ((any: any) => any) | null;
    shrink?: boolean;
    setHover?: (h: boolean) => void;
    setPressed?: (p: boolean) => void;
} & { [key: string]: any }; // FIXME: Should be PressableProps

// Hover events can be tested in demos using the HOVER action
export function HoverView({
    style,
    ariaLabel,
    ariaText,
    testID,
    role,
    hoverStyle,
    pressedStyle,
    children,
    disabled,
    onPress,
    shrink,
    setHover,
    setPressed,
    ...props
}: HoverViewProps) {
    const [localHover, setLocalHover] = useState(false);
    const [localPressed, setLocalPressed] = useState(false);
    const [accessibilityPress, setAccessibilityPress] = useState(false);

    const handleLayout = (event) => {
        const { height, width } = event.nativeEvent.layout || {};
        if (height <= MIN_HEIGHT_CLICKZONE || width <= MIN_WIDTH_CLICKZONE) {
            setAccessibilityPress(true);
        }
    };
    const tAriaLabel = useTranslation(ariaLabel);

    if (!onPress) {
        return (
            <View style={[style, shrink ? { flexShrink: 1 } : null]} testID={makeTestID({ testID, ariaLabel })}>
                {children}
            </View>
        );
    }
    return (
        <Pressable
            aria-disabled={disabled}
            aria-label={tAriaLabel ?? ariaText}
            disabled={disabled}
            onHoverIn={() => {
                setLocalHover(true);
                setHover?.(true);
            }}
            onHoverOut={() => {
                setLocalHover(false);
                setHover?.(false);
            }}
            onLayout={handleLayout}
            onPress={onPress}
            onPressIn={() => {
                setLocalPressed(true);
                setPressed?.(true);
            }}
            onPressOut={() => {
                setLocalPressed(false);
                setPressed?.(false);
            }}
            role={role}
            style={[style, localHover ? hoverStyle : null, localPressed ? pressedStyle : null, shrink ? { flexShrink: 1 } : null]}
            testID={makeTestID({ testID, ariaLabel })}
            {...props}
        >
            {children}
            {accessibilityPress && <InvisibleExtendedClickArea />}
        </Pressable>
    );
}

function InvisibleExtendedClickArea() {
    const s = InvisibleExtendedClickAreaStyle;
    return <View aria-hidden style={s.clickArea} />;
}

// HACK: React-native doesn't support non-numeric positions.
const InvisibleExtendedClickAreaStyle = StyleSheet.create({
    clickArea: {
        position: 'absolute',
        left: '50%' as any,
        top: '50%' as any,
        width: MIN_WIDTH_CLICKZONE,
        height: MIN_HEIGHT_CLICKZONE,
        transform: 'translate(-50%, -50%)' as any,
        minWidth: '100%' as any,
        minHeight: '100%' as any,
    },
});

export type CardProps = { testID?: string; children: React.ReactNode };

export function Card({ testID, children }: CardProps) {
    const s = CardStyle;
    return (
        <View style={s.card} testID={testID}>
            {children}
        </View>
    );
}

const CardStyle = StyleSheet.create({
    card: {
        paddingBottom: 20,
        backgroundColor: colorSurfacePrimary,
        borderBottomColor: colorBorderPrimary,
        borderBottomWidth: 1,
    },
});

export type PadBoxProps = {
    children: React.ReactNode;
    horiz?: number;
    vert?: number;
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
};

export function PadBox({ children, horiz, vert, top, bottom, left, right }: PadBoxProps) {
    return (
        <View
            style={{
                paddingHorizontal: horiz,
                paddingVertical: vert,
                paddingTop: top,
                paddingBottom: bottom,
                paddingLeft: left,
                paddingRight: right,
            }}
        >
            {children}
        </View>
    );
}

export type HorizBoxProps = {
    children: React.ReactNode;
    gap?: number;
    center?: boolean;
    shrink?: boolean;
    right?: boolean;
    spread?: boolean;
    wide?: boolean;
    role?: Role;
    testID?: string;
};

export function HorizBox({ children, gap, center, shrink, right, spread, wide, role, testID }: HorizBoxProps) {
    return (
        <View
            role={role}
            style={{
                flexDirection: 'row',
                alignItems: center ? 'center' : 'flex-start',
                justifyContent: spread ? 'space-between' : right ? 'flex-end' : 'flex-start',
                flexShrink: shrink ? 1 : 0,
                width: wide ? '100%' : undefined,
                gap,
            }}
            testID={testID}
        >
            {children}
        </View>
    );
}

export type VertBoxProps = { children: React.ReactNode; gap?: number; wide?: boolean; center?: boolean; role?: Role; testID?: string };

export function VertBox({ children, gap, wide = false, center = false, role, testID }: VertBoxProps) {
    return (
        <View
            role={role}
            style={{
                flexDirection: 'column',
                alignItems: center ? 'center' : wide ? undefined : 'flex-start',
                width: wide ? '100%' : null,
                gap,
            }}
            testID={testID}
        >
            {children}
        </View>
    );
}

export const LoadingScreenStyle = StyleSheet.create({
    outer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    text: {
        color: '#666',
    },
});

export type NarrowProps = { children: React.ReactNode; pad?: boolean };

export function Narrow({ children, pad = true }: NarrowProps) {
    return (
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', paddingVertical: pad ? 20 : undefined }}>
            <View style={{ maxWidth: 600, flexShrink: 1, flexGrow: 1, marginHorizontal: pad ? 20 : undefined }}>{children}</View>
        </View>
    );
}

export type CenterProps = { children: React.ReactNode; pad?: number };

export function Center({ children, pad = 0 }: CenterProps) {
    return <View style={{ alignSelf: 'center', alignItems: 'center', margin: pad }}>{children}</View>;
}

export type AdaptiveWidthProps = {
    children: React.ReactNode;
    setNarrow: (narrow: boolean) => void;
    breakpoint?: number;
    style?: StyleProp<ViewStyle>;
};

export function AdaptiveWidth({ children, setNarrow, breakpoint = 540, style }: AdaptiveWidthProps) {
    const handleLayout = useCallback(
        (event) => {
            const { width } = event.nativeEvent.layout;
            setNarrow(width < breakpoint);
        },
        [setNarrow, breakpoint],
    );

    if (React.Children.count(children) === 1 && React.isValidElement(children)) {
        const child = React.Children.only(children) as React.ReactElement;
        return React.cloneElement(child, {
            onLayout: handleLayout,
            style: [child.props.style, style],
        });
    }
    return (
        <View onLayout={handleLayout} style={style}>
            {children}
        </View>
    );
}
export type HeaderBoxProps = {
    children: React.ReactNode;
    backgroundColor?: string;
    horiz?: number;
    vert?: number;
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
};

export function HeaderBox({
    children,
    backgroundColor = colorSurfaceHighlight,
    horiz = 20,
    vert = 20,
    top,
    bottom,
    left,
    right,
}: HeaderBoxProps) {
    return (
        <View
            style={{
                paddingHorizontal: horiz,
                paddingVertical: vert,
                paddingTop: top,
                paddingBottom: bottom,
                paddingLeft: left,
                paddingRight: right,
                backgroundColor,
            }}
        >
            {children}
        </View>
    );
}

export type SeparatedBoxProps = { children: React.ReactNode; gap?: number; separator?: typeof Separator };

export function SeparatedBox({ children, gap = 0, separator = Separator }: SeparatedBoxProps) {
    const childrenArray = React.Children.toArray(children);

    return (
        <View style={{ flexDirection: 'column', gap }}>
            {childrenArray.map((child, index) => (
                <React.Fragment key={index}>
                    {child}
                    {index < childrenArray.length - 1 && React.createElement(separator)}
                </React.Fragment>
            ))}
        </View>
    );
}

export function Separator() {
    const s = SeparatorStyle;
    return <View style={s.separator} />;
}

const SeparatorStyle = StyleSheet.create({
    separator: {
        height: 1,
        backgroundColor: colorBorderPrimary,
    },
});

export function WindowTitle({ label = undefTL, text = undefS, formatParams = undefFP }) {
    const tLabel = useTranslation(label, formatParams);
    setTitle(tLabel ?? text);
    return null;
}

export type HoverSelectBoxProps = { selected: boolean; testID: string; onPress: () => void; children: React.ReactNode };

// HACK: Bump the key to force a re-render, otherwise the old hover state is preserved
// when the component is unselected, and potentially moves to longer be under the mouse
// without getting a hover out event
export function HoverSelectBox({ selected, testID, onPress, children }: HoverSelectBoxProps) {
    const s = HoverSelectBoxStyle;
    const [key, setKey] = useState(0);
    function onLocalPress() {
        onPress();
        setKey(key + 1);
    }
    return (
        <HoverView disabled={!onPress || selected} hoverStyle={s.hover} key={key} onPress={onLocalPress} style={s.outline} testID={testID}>
            {children}
        </HoverView>
    );
}
const HoverSelectBoxStyle = StyleSheet.create({
    hover: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
    },
    outline: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
    },
});

export type ShadowBoxProps = { children: React.ReactNode };

export function ShadowBox({ children }: ShadowBoxProps) {
    const s = ShadowBoxStyle;
    return <View style={s.shadow}>{children}</View>;
}

const ShadowBoxStyle = StyleSheet.create({
    shadow: {
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        borderRadius: 12,
    },
});

export type FlowBoxProps = {
    children: React.ReactNode;
    center?: boolean;
    rowGap?: number;
    columnGap?: number;
    style?: StyleProp<ViewStyle>;
};

export function FlowBox({ children, center = false, rowGap, columnGap, style }: FlowBoxProps) {
    return (
        <View
            style={[
                {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: center ? 'center' : 'flex-start',
                    rowGap,
                    columnGap,
                },
                style,
            ]}
        >
            {children}
        </View>
    );
}

export type ContainerProps = { children: React.ReactNode };

export function Container({ children }: ContainerProps) {
    const s = ContainerStyle;
    return <View style={s.outer}>{children}</View>;
}

const ContainerStyle = StyleSheet.create({
    outer: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 12,
    },
});
